/**
 * Mobile Welcome Tour Component
 *
 * A mobile-specific onboarding tour using react-joyride that introduces
 * new users to the mobile interface and key features.
 *
 * Features:
 * - Targets mobile-specific UI elements (bottom nav, header, etc.)
 * - Only shown on mobile devices (below lg breakpoint)
 * - Tracks completion in database for authenticated users
 * - Falls back to localStorage for anonymous users
 * - Can be restarted from user menu
 *
 * @author Ellis Reed
 */

"use client";

import React, { useState, useEffect, useCallback } from "react";
import Joyride, {
  CallBackProps,
  STATUS,
  Step,
  ACTIONS,
  EVENTS,
} from "react-joyride";
import { useSession } from "next-auth/react";
import { completeMobileTour, skipTour } from "@/app/actions/user-preferences";

interface MobileWelcomeTourProps {
  onComplete?: () => void;
  shouldRun?: boolean;
}

export function MobileWelcomeTour({
  onComplete,
  shouldRun = false,
}: MobileWelcomeTourProps) {
  const { data: session } = useSession();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Start tour when shouldRun becomes true and we're on mobile
  // Note: Tour now shows beacons but doesn't auto-start - user must click beacon to start
  useEffect(() => {
    if (shouldRun && isMobile) {
      // Don't auto-start - just set up beacons without running
      // Users must click on beacons to start the tour
      setRun(false);
    }
  }, [shouldRun, isMobile]);

  // Mobile-specific tour steps
  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="space-y-3">
          <h2 className="text-xl font-bold text-gray-900">
            Welcome to USLand!
          </h2>
          <p className="text-gray-700 text-sm">
            Let&apos;s take a quick tour of the mobile app. We&apos;ll show you
            how to navigate and find what you need.
          </p>
          <p className="text-xs text-gray-500">
            Tap &quot;Next&quot; to continue or &quot;Skip&quot; to explore on
            your own.
          </p>
        </div>
      ),
      placement: "center",
      // Beacon enabled so users see flashing dots for points of interest
      disableBeacon: false,
    },
    {
      target: '[data-tour="mobile-nav"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Bottom Navigation
          </h3>
          <p className="text-gray-700 text-sm">
            This is your main navigation bar. All key features are just one tap
            away.
          </p>
          <p className="text-xs text-gray-600">
            Five quick-access tabs for everything you need.
          </p>
        </div>
      ),
      placement: "top",
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="mobile-nav-home"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Home</h3>
          <p className="text-gray-700 text-sm">
            Return to the homepage anytime to see featured properties and
            updates.
          </p>
        </div>
      ),
      placement: "top",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="mobile-nav-search"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Search</h3>
          <p className="text-gray-700 text-sm">
            Find commercial properties using our powerful search with filters
            and interactive map.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-2">
            <p className="text-xs text-blue-900">
              <strong>Tip:</strong> Search by city, state, ZIP code, or property
              type.
            </p>
          </div>
        </div>
      ),
      placement: "top",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="mobile-nav-saved"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Saved Properties
          </h3>
          <p className="text-gray-700 text-sm">
            Tap the heart icon on any property to save it here for quick access
            later.
          </p>
          <p className="text-xs text-gray-600">
            Your favorites are synced across all your devices when signed in.
          </p>
        </div>
      ),
      placement: "top",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="mobile-nav-dashboard"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Dashboard</h3>
          <p className="text-gray-700 text-sm">
            Access your personal dashboard to manage listings, view analytics, and track activity.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-2">
            <p className="text-xs text-blue-900">
              <strong>Quick access:</strong> Your command center is now just one tap away!
            </p>
          </div>
        </div>
      ),
      placement: "top",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="mobile-nav-more"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">More Options</h3>
          <p className="text-gray-700 text-sm">
            Tap here to access your profile, create listings, and more settings.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-2">
            <p className="text-xs text-blue-900">
              <strong>Tip:</strong> Tap the More button now to see what&apos;s inside!
            </p>
          </div>
        </div>
      ),
      placement: "top",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="mobile-more-profile"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Profile</h3>
          <p className="text-gray-700 text-sm">
            Edit your profile, update contact information, and manage your account settings.
          </p>
        </div>
      ),
      placement: "top",
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="mobile-more-listings"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">My Listings</h3>
          <p className="text-gray-700 text-sm">
            View and manage all your commercial property listings. Edit details, upload photos, and track views.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-md p-2 mt-2">
            <p className="text-xs text-green-900">
              <strong>Manage:</strong> Control all your listings from the mobile app!
            </p>
          </div>
        </div>
      ),
      placement: "top",
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="mobile-header-logo"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">USLand Logo</h3>
          <p className="text-gray-700 text-sm">
            Tap the logo anytime to return to the homepage.
          </p>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="header-messages"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
          <p className="text-gray-700 text-sm">
            Quick access to your messages. Chat directly with property owners and brokers in real-time.
          </p>
          <div className="bg-cyan-50 border border-cyan-200 rounded-md p-2 mt-2">
            <p className="text-xs text-cyan-900">
              <strong>Features:</strong> Instant messaging, notifications, and messages linked to properties.
            </p>
          </div>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="theme-toggle"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Theme Toggle</h3>
          <p className="text-gray-700 text-sm">
            Switch between light and dark mode for comfortable viewing.
          </p>
        </div>
      ),
      placement: "bottom",
      spotlightPadding: 4,
    },
    {
      target: '[data-tour="bug-report"]',
      content: (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-red-600">
            Bug Report (BETA)
          </h3>
          <p className="text-gray-700 text-sm">
            <strong>Found a problem?</strong> Please report it! Your feedback
            helps us improve.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-md p-2">
            <p className="text-xs text-red-900">
              Tap the pulsing bug button to report any issues, suggest features,
              or give feedback.
            </p>
          </div>
        </div>
      ),
      placement: "left",
      spotlightPadding: 8,
    },
    {
      target: "body",
      content: (
        <div className="space-y-3">
          <h2 className="text-xl font-bold text-gray-900">
            You&apos;re Ready!
          </h2>
          <p className="text-gray-700 text-sm">
            That&apos;s the complete mobile tour! You now know all the key features
            of USLand on mobile.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2">
            <p className="text-xs font-medium text-blue-900">Quick Recap:</p>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Use <strong>Search</strong> to find properties</li>
              <li>• Tap <strong>Saved</strong> to access favorites</li>
              <li>• Tap <strong>Dashboard</strong> to manage your listings</li>
              <li>• Use <strong>Messages</strong> (top header) to contact owners</li>
              <li>• Tap <strong>More</strong> for Profile, My Listings, and settings</li>
              <li>• <strong>Report bugs</strong> using the pulsing bug button in More menu</li>
            </ul>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-md p-2 mt-2">
            <p className="text-xs text-green-900">
              <strong>Tip:</strong> Sign in to unlock all features like creating listings
              and managing your dashboard!
            </p>
          </div>
          <p className="text-xs text-gray-500">
            You can restart this tour from the More menu anytime.
          </p>
        </div>
      ),
      placement: "center",
    },
  ];

  const handleTourComplete = useCallback(async () => {
    // Save to database for authenticated users
    await completeMobileTour();

    // Also save to localStorage as fallback
    if (typeof window !== "undefined") {
      localStorage.setItem("usland-mobile-tour-completed", "true");
      localStorage.removeItem("usland-mobile-tour-skipped");
    }

    onComplete?.();
  }, [onComplete]);

  const handleTourSkip = useCallback(async () => {
    // Save to database for authenticated users
    await skipTour("mobile");

    // Also save to localStorage as fallback
    if (typeof window !== "undefined") {
      localStorage.setItem("usland-mobile-tour-skipped", "true");
    }

    onComplete?.();
  }, [onComplete]);

  const handleJoyrideCallback = useCallback(
    async (data: CallBackProps) => {
      const { status, action, index, type } = data;
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      if (finishedStatuses.includes(status)) {
        setRun(false);

        if (status === STATUS.FINISHED) {
          await handleTourComplete();
        } else if (status === STATUS.SKIPPED) {
          await handleTourSkip();
        }
      } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        // Update step index
        const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        setStepIndex(nextStepIndex);
      }
    },
    [handleTourComplete, handleTourSkip]
  );

  // Restart tour function
  const restartTour = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("usland-mobile-tour-completed");
      localStorage.removeItem("usland-mobile-tour-skipped");
    }
    setStepIndex(0);
    setRun(true);
  }, []);

  // Listen for restart event
  useEffect(() => {
    const handleRestartTour = () => {
      if (isMobile) {
        restartTour();
      }
    };

    window.addEventListener("restart-mobile-tour", handleRestartTour);
    return () =>
      window.removeEventListener("restart-mobile-tour", handleRestartTour);
  }, [isMobile, restartTour]);

  // Don't render anything on desktop
  if (!isMobile) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: "#0066cc",
          textColor: "#1f2937",
          backgroundColor: "#ffffff",
          overlayColor: "rgba(0, 0, 0, 0.6)",
          arrowColor: "#ffffff",
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 12,
          padding: 16,
          maxWidth: "90vw",
        },
        tooltipContainer: {
          textAlign: "left",
        },
        buttonNext: {
          backgroundColor: "#0066cc",
          fontSize: 14,
          fontWeight: 600,
          padding: "10px 20px",
          borderRadius: 8,
        },
        buttonBack: {
          color: "#6b7280",
          fontSize: 14,
          marginRight: 8,
        },
        buttonSkip: {
          color: "#9ca3af",
          fontSize: 14,
        },
        spotlight: {
          borderRadius: 12,
        },
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        },
      }}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        open: "Open",
        skip: "Skip tour",
      }}
      floaterProps={{
        disableAnimation: false,
        styles: {
          floater: {
            filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))",
          },
        },
      }}
      disableScrolling
      disableOverlayClose
      spotlightClicks
    />
  );
}

/**
 * Helper function to restart mobile tour from anywhere in the app
 */
export function restartMobileTour() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("restart-mobile-tour"));
  }
}
